/* eslint-disable max-len */
import {
  Avatar,
  AvatarGroup,
  Box,
  Collapse,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { DownloadIcon, FileText, GraduationCap, PencilRuler, Share2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent
} from "react";
import { useTranslation } from "react-i18next";
import type { LearningActivity, ReferencedLearningActivity } from "types";
import { ActivityItem } from "./ActivityDotNavigation.types";
import {
  CardContent,
  CardWrapper,
  CtaContainer,
  MetadataRow,
  StatusBox,
  StepIndicator,
  SubtitleTypography,
  TitleRow,
  TitleTypography
} from "./CourseActivityCard.styles";
import type { CourseActivityCardProps } from "./CourseActivityCard.types";

import { isNotClassroom } from "./utils";
import { HiddenCTA } from "../../components/CarouselCardRestyle/HiddenCTA.component";
import { useActivityAndCourseSelection } from "../../components/DetailsEsploraRestyle/IndexActivitySelected.context";
import { LearningActivityModal } from "../../components/LearningActiviyModal/LearningActivityModal.component";
import { ctaDisabled } from "../../components/LearningActiviyModal/utils";
import { POST_TYPE } from "../../consts";
import { useType } from "../../hooks";
import { useDownloadCertificate, useEditionsQuery } from "../../queries";
import { useAuthStore, useCommunityClusterStore, useCurrentCommunityCluster } from "../../stores";
import { formatDuration_h_m, hasPassedExpiration } from "../../utils/datetime";
import { downloadBlobFile } from "../../utils/general";
import { StatusBoxesContainer } from "../CarouselCardRestyle/CarouselCard.style";
import { CreateContentModal } from "../CreateContentModal";
import { NumberLOTypography } from "../PathCourseAccordion/PathCourseAccordion.styles";

function getInitials(completeName: string): string {
  return completeName
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase())
    .slice(0, 2)
    .join("");
}

function parsePercentage(value?: string): number {
  if (!value) return 0;
  const parsed = parseInt(value.replace("%", ""), 10);
  return isNaN(parsed) ? 0 : parsed;
}

export function CourseActivityCard({
  activity,
  attributes,
  canAccessNow,
  certificate,
  courseId,
  disabled,
  duration,
  ecmRegistration,
  ecm_specialization,
  enrollType,
  expirationDate,
  externalFileUrl,
  grandParentId,
  id,
  index,
  isActive = false,
  isEditorial,
  isFavourite,
  isLinkRelative,
  isMandatory,
  isStandAlone,
  isSurvey,
  isTest,
  learningObjectType,
  learningObjectTypology,
  link,
  madeAttempts,
  parentId,
  percentageOfCompletion,
  rootId,
  setOpenModalEcm,
  status,
  teachers,
  title,
  toolNumber,
  isStandAloneActivity,
  onMouseEnter,
  handleActivityClick,
  initialActiveLoId
}: CourseActivityCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(500));
  const typeObjectLearning = useType(learningObjectType, learningObjectTypology);
  const anchorElRef = useRef<HTMLDivElement | null>(null);

  const [commentsModalIsOpen, setCommentModalIsOpen] = useState(false);
  const [openEdition, setOpenEdition] = useState(false);
  const [_isDownloading, setIsDownloading] = useState(false);

  const { availableClusters, currentCluster } = useCurrentCommunityCluster();
  const { setCurrentCluster } = useCommunityClusterStore();
  const userData = useAuthStore(state => state.userData);

  const { mutateAsync: mutateDownloadCertificate } = useDownloadCertificate();

  const { activityIdSelected, setActivityIdSelected, syncUrlToActivity } = useActivityAndCourseSelection();

  const sharingEnabled = !!availableClusters.find((cluster) => cluster.is_share_enabled);
  const showModalEcm = setOpenModalEcm && disabled && ecm_specialization && !ecmRegistration;

  if (!currentCluster?.is_share_enabled && sharingEnabled) {
    setCurrentCluster(availableClusters.find((cluster) => cluster.is_share_enabled));
  }

  const handleClick = useCallback(() => {
    if(activity?.id !== activityIdSelected) {
      setActivityIdSelected(activity?.id ?? undefined);
      syncUrlToActivity(activity as ActivityItem, isStandAloneActivity);
    }
  }, [activity, handleActivityClick, syncUrlToActivity]);

  useEffect(() => {
    initialActiveLoId && setActivityIdSelected(initialActiveLoId);
  }, [initialActiveLoId]);

  const handleDownloadCertificate = async () => {
    if (!certificate?.isEnable || !certificate?.id || !userData?.id_user) return;
    setIsDownloading(true);
    try {
      const blob = await mutateDownloadCertificate({
        certificateId: certificate.id,
        learnerId: userData.id_user as number,
        learningObjectId: id
      });
      downloadBlobFile({
        blobFile: blob,
        fileName: `issued_certificate_lo_${id}_learner_${userData.id_user}.pdf`
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const isCompleted = status === "C";
  const isExpired = hasPassedExpiration(expirationDate);
  //const isTestCompleted = (isTest || isSurvey) && isCompleted;
  const percentage = parsePercentage(percentageOfCompletion);

  const typologyLabel = isTest ? t("test") : isSurvey ? t("survey") : typeObjectLearning;

  // Aria label for accessibility (same logic as CarouselCardRestyle)
  let ariaLabel = title;
  if (typologyLabel) {
    ariaLabel = `${title}. ${t("typology_carousel")}:${typologyLabel}`;
  }
  if (percentage > 0) {
    ariaLabel = `${ariaLabel}. ${t("completion_percentage")} ${percentageOfCompletion}`;
  }
  if (duration) {
    ariaLabel = `${ariaLabel}. ${t("duration")}:${duration}`;
  }
  if (isCompleted) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("completed")}`;
  } else if (isExpired) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("expired")}`;
  } else if (isMandatory) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("mandatory")}`;
  }

  useEffect(() => {
    isActive && syncUrlToActivity(activity as ActivityItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: editions, isLoading: editionsLoading, refetch: refetchEditions } = useEditionsQuery({ classroomId: activity?.id.toString() ?? "", enabled: false });
  const hasEditions = editions && editions.length > 0;
  const enrolled = editions?.some(edition =>  edition.status === "E") ?? false;
  const isWaitingTopDownEnrollment = hasEditions && !enrolled;
  
  useEffect(() => {
    if (activityIdSelected == activity?.id && !isNotClassroom(learningObjectTypology)) {
      refetchEditions();
    }
  }, [activityIdSelected, refetchEditions, learningObjectTypology, activity?.id]);

  return (
    <CardWrapper
      role="region"
      className={ "course-activity-card" }
      tabIndex={ 0 }
      aria-label={ ariaLabel }
      $isActive={  activityIdSelected === activity?.id }
      $isComplete={ isCompleted }
      onClick={ (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        handleClick();
        onMouseEnter && onMouseEnter(activity?.id);
        if (showModalEcm) {
          setOpenModalEcm(true);
        }
      } }
      onKeyDown={ (e: KeyboardEvent<HTMLElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();

          if (showModalEcm) {
            setOpenModalEcm?.(true);
          }
        }
      } }
    >
      { /* Step indicator with circular progress */ }
      { /* Card body */ }
      <CardContent
        isStart={ true }
        id={ `card-content-${activity?.id}` }
        item
        xs
        width={ "100%" }
        gap={ "1rem" }
      >
        <Stack className="info-row"
          flexDirection={ "row" }
          alignItems={ "center" }
          gap={ "1rem" }
          width={ "100%" }
          sx={ {
            cursor: "pointer"
          } }
        >
          <StepIndicator>
            <Box position="relative" display="inline-flex">
              <Box alignSelf="center" display="flex" flexShrink={ 0 }>
                <NumberLOTypography
                  sx={ {
                    minWidth: 24,
                    textAlign: "center"
                  } }>
                  { index }
                </NumberLOTypography>
              </Box>
            </Box>
          </StepIndicator>
          <Stack
            flex={ 1 }
            minWidth={ 0 }
          >
            <TitleRow>
              <TitleTypography
                color={ theme.customColors.exploreCardText }
              >
                { title }
              </TitleTypography>

            </TitleRow>

            <SubtitleTypography
              color={ theme.customColors.exploreCardText }
            >
              { typologyLabel }
              { duration ? ` | ${formatDuration_h_m(duration)}` : "" }
            </SubtitleTypography>
          </Stack>

        </Stack>
      </CardContent>

      <StatusBox>
        <StatusBoxesContainer
          attributes={ attributes }
          endDate={ undefined }
          expirationDate={ expirationDate }
          hideMandatoryAndAttributes
          id={ id }
          isCompleted={ isCompleted }
          inProgress={ status === "P" }
          isExpired={ isExpired }
          isMandatory={ isMandatory }
          learningObjectTypology={ learningObjectTypology }
          startDate={ undefined }
        />
      </StatusBox>
      <Box
        className="card-bottom-box"
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        flexShrink={ 0 }
        justifyContent="space-between"
        alignSelf="stretch"
        sx={ {
          mt: "auto",
          width: "100%",
          paddingLeft: "8px"
        } }
      >
        <Collapse
          in={ activityIdSelected === activity?.id }
          timeout={ 150 }
          easing="ease-in-out"
          sx={ {
            width: "100%",
            "& .MuiCollapse-wrapper": {
              width: "100%"
            },
            "& .MuiCollapse-wrapperInner": {
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: "1rem",
              transition: "all .15s ease-in-out",
              [theme.breakpoints.up("sm")]: {
                flexWrap: "nowrap"
              }
            }
          } }
        >
          <MetadataRow
            id={ `meta-data-activity-${id}` }
          >
            { activity && activity?.editions?.length > 0 && (
              <Stack direction="row" alignItems="center" gap={ 0.5 }>
                <PencilRuler
                  aria-hidden="true"
                  size={ "1rem" }
                  color={ theme.customColors.exploreCardText }
                />
                <Typography
                  fontSize={ "0.75rem" }
                  variant="body2"
                  color={ theme.customColors.exploreCardText }
                >
                  { activity?.editions?.length } { " " } { t("editions") }
                </Typography>
              </Stack>
            ) }
            { toolNumber > 0 && !editionsLoading && !isWaitingTopDownEnrollment && (
              <Stack direction="row" alignItems="center" gap={ 0.5 }>
                <FileText
                  aria-hidden="true"
                  size={ "1rem" }
                  color={ theme.customColors.exploreCardText }
                />
                <Typography
                  fontSize={ "0.75rem" }
                  variant="body2"
                  color={ theme.customColors.exploreCardText }
                >
                  { t("resource", { count: toolNumber }) }
                </Typography>
              </Stack>
            ) }

            { teachers && teachers.length > 0 && !editionsLoading && !isWaitingTopDownEnrollment && (
              <Stack direction="row" alignItems="center" gap={ 1 }>
                <GraduationCap
                  aria-hidden="true"
                  size={ "1rem" }
                  color={ theme.customColors.exploreCardText }
                />
                <Typography
                  variant="body2"
                  fontSize={ "0.75rem" }
                  color={ theme.customColors.exploreCardText }>
                  { t("teachers") }
                </Typography>
                <AvatarGroup
                  max={ 4 }
                  sx={ {
                    "& .MuiAvatar-root": {
                      border: `2px solid ${theme.customColors.backgroundPrimary}`,
                      fontSize: "0.5rem",
                      height: "1.25rem",
                      lineHeight: "0.75rem",
                      width: "1.25rem"
                    }
                  } }
                >
                  { teachers.map((teacher) => (
                    <Avatar
                      key={ teacher.id }
                      alt={ teacher.completeName }
                      src={ teacher.image || undefined }
                      sx={ {
                        bgcolor: theme.customColors.backgroundPrimaryCta
                      } }
                    >
                      { getInitials(teacher.completeName) }
                    </Avatar>
                  )) }
                </AvatarGroup>
              </Stack>
            ) }

            <Stack
              flexDirection={ "row" }
              gap="0.75rem"
            >
              { sharingEnabled && (
                <Share2
                  size={ "1.25rem" }
                  color={ theme.customColors.exploreCardText }
                  style={ { cursor: "pointer" } }
                  aria-label={ t("share_comment") }
                  role="button"
                  tabIndex={ 0 }
                  onClick={ (e) => {
                    e.stopPropagation();
                    setCommentModalIsOpen(true);
                  } }
                />
              ) }

              { certificate && (
                <DownloadIcon
                  size={ "1.25rem" }
                  color={ certificate.isEnable
                    ? theme.customColors.exploreCardText
                    : theme.customColors.border }
                  style={ {
                    cursor: certificate.isEnable ? "pointer" : "not-allowed"
                  } }
                  aria-label={ t("download") }
                  role="button"
                  tabIndex={ 0 }
                  onClick={ (e) => {
                    e.stopPropagation();
                    handleDownloadCertificate();
                  } }
                />
              ) }
            </Stack>
          </MetadataRow>
          <CtaContainer sx={ {
            "& .card-cta": {
              gap: 0
            },
            "& .card-cta > .MuiStack-root:empty": {
              display: "none"
            },
            "& .collapse_cta": {
              gap: 0,
              height: "auto",
              opacity: 1
            },
            alignItems: "flex-end",
            flexDirection: "column",
            gap: 0
          } }>
            <Stack
              direction="row"
              spacing="0.5rem"
              gap={ isMobile ? "0.5rem" : "unset" }
              flexWrap={ "wrap" }
            >
              { isNotClassroom(learningObjectTypology) ? (
                <>
                  <HiddenCTA
                    attributes={ attributes }
                    canAccessNow={ canAccessNow }
                    courseId={ courseId }
                    disabled={ ctaDisabled(activity as LearningActivity) || disabled }
                    ecm_specialization={ ecm_specialization ? ecm_specialization : false }
                    ecmRegistration={ ecmRegistration ? ecmRegistration : false }
                    enrollType={ enrollType }
                    externalFileUrl={ externalFileUrl }
                    grandParentId={ grandParentId ? grandParentId : undefined }
                    id={ id }
                    isCard={ false }
                    isEditorial={ Boolean(isEditorial) }
                    isLinkRelative={ isLinkRelative }
                    isStandAlone={ isStandAlone }
                    isSurvey={ isSurvey }
                    isHeroBanner={ false }
                    isTest={ isTest }
                    learningObjectType={ learningObjectType }
                    learningObjectTypology={ learningObjectTypology }
                    link={ link }
                    //TODO isfavourite lasciamo a false?
                    isFavourite={ !!isFavourite }
                    madeAttempts={ madeAttempts }
                    parentId={ parentId ? parentId : undefined }
                    percentageOfCompletion={ percentageOfCompletion }
                    rootId={ rootId }
                    showDetailsButton={ false }
                    status={ status || "N" }
                  />
                  { !isEditorial &&
                    <LearningActivityModal open={ openEdition } setOpen={ setOpenEdition } buttonType={ "secondary" } activity={ activity as ReferencedLearningActivity }></LearningActivityModal> }
                </>
              ) : (
                <LearningActivityModal
                  open={ openEdition }
                  setOpen={ setOpenEdition }
                  buttonType={ "primary" }
                  buttonLabel={ t("editions_show") }
                  activity={ activity as ReferencedLearningActivity }
                  initialEditions={ editions }
                />
              ) }
            </Stack>
          </CtaContainer>
        </Collapse>
      </Box>

      { commentsModalIsOpen && (
        <CreateContentModal
          selectedFilter={ null }
          selectedTopic={ null }
          anchorOrigin={ {
            horizontal: "center",
            vertical: "center"
          } }
          transformOrigin={ {
            horizontal: "center",
            vertical: "center"
          } }
          anchorEl={ anchorElRef.current }
          closeDrawer={ () => setCommentModalIsOpen(false) }
          onClose={ () => setCommentModalIsOpen(false) }
          open={ commentsModalIsOpen }
          postData={ {
            loAuthor: "",
            loId: id.toString(),
            loType: learningObjectTypology
          } }
          postType={ POST_TYPE.SHARED }
        />
      ) }
    </CardWrapper>
  );
}
